/**
 * คู่ฝึก (data/sparring.ts): which tiers the player has beaten, the gate on the next, and what a win pays. Pure
 * rules over `PlayerProgress.sparring` (absent until the first win); the day is the arena's (`progress.arena.day`,
 * set from the calendar by the client, by the server online), so the daily cap turns with the arena's day.
 */
import { SPAR_TIERS, SPARRING } from './data/sparring';
import type { PassReward } from './data/pass';
import type { PlayerProgress } from './progress';

export interface SparringState {
  /** Tier ids beaten at least once. */
  beaten: string[];
  /** The day `wins` counts for, and the paid repeat wins that day. */
  day: string;
  wins: number;
}

export const newSparring = (): SparringState => ({ beaten: [], day: '', wins: 0 });

/** A saved state repaired against the data. */
export function parseSparring(v: unknown): SparringState {
  const r = (v && typeof v === 'object' ? v : {}) as Partial<SparringState>;
  const ids = new Set(SPAR_TIERS.map((t) => t.id));
  return {
    beaten: Array.isArray(r.beaten) ? [...new Set(r.beaten.filter((x): x is string => typeof x === 'string' && ids.has(x)))] : [],
    day: typeof r.day === 'string' ? r.day : '',
    wins: typeof r.wins === 'number' && Number.isFinite(r.wins) && r.wins > 0 ? Math.floor(r.wins) : 0,
  };
}

/** Whether a tier (index into `SPAR_TIERS`) is locked: the one before it not yet beaten. */
export function sparLocked(p: PlayerProgress, k: number): boolean {
  if (k <= 0) return false;
  const prev = SPAR_TIERS[k - 1];
  return !prev || !(p.sparring?.beaten.includes(prev.id) ?? false);
}

/** Whether a tier has been beaten. */
export const sparBeaten = (p: PlayerProgress, k: number): boolean =>
  !!SPAR_TIERS[k] && (p.sparring?.beaten.includes(SPAR_TIERS[k].id) ?? false);

/** Paid repeat wins left today. */
export function sparWinsLeft(p: PlayerProgress): number {
  const s = p.sparring;
  return SPARRING.dailyWins - (s && s.day === p.arena.day ? s.wins : 0);
}

/**
 * A win over tier `k`: marks it beaten and says what it pays (the caller grants it): the first win's reward once,
 * then the repeat reward while today's cap lasts, else nothing (null).
 */
export function sparWin(p: PlayerProgress, k: number): { reward: PassReward; first: boolean } | null {
  const t = SPAR_TIERS[k];
  if (!t) return null;
  const s = (p.sparring ??= newSparring());
  if (!s.beaten.includes(t.id)) {
    s.beaten.push(t.id);
    return { reward: t.first, first: true };
  }
  if (s.day !== p.arena.day) {
    s.day = p.arena.day;
    s.wins = 0;
  }
  if (s.wins >= SPARRING.dailyWins) return null;
  s.wins++;
  return { reward: t.repeat, first: false };
}
